import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Layers,
  CheckCircle2,
  ChevronDown,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { api } from "../../redux/apis/api";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import { canAdd, resolvePolicy } from "./benefit-policy";
import { recomputeBillTotal } from "./billing-totals";

// SchemePanel — billing-footer widget. On every cart change, debounced POST to
// /api/schemes/evaluate with the current bill snapshot. Renders a dropdown of
// applicable schemes (backend already sorts by priority DESC), auto-selects
// the top one, and writes the discount through setFieldValue.
//
// Employee schemes need bill.employee, which the cashier sets via the inline
// "Verify employee" subpanel (Send OTP → enter OTP → Verify). Once verified,
// the cart stamps bill.employee and the next evaluate() round picks the
// employee scheme into applicable[].
//
// Persistence: selection + employee + applied bill_offer_application id are
// persisted to localStorage keyed by billRef so a page refresh restores the
// cashier's state without re-verifying or re-picking.
//
// Audit row write: when billRef is set AND a scheme is selected, the panel
// POSTs once to /api/schemes/apply which records the bill_offer_application
// row server-side (re-evaluates the verdict on the server, so a tampered
// client can't fake a discount). NOT atomic with the bill-insert transaction
// — if the bill row exists but /apply fails, the cashier sees an error and
// can retry. True atomicity needs salesOrderService to call applyAtBill
// directly with its own client (tracked separately).

// Module-level cache of verified employees keyed by customer signature.
// In-memory only — survives panel mount/unmount within a single page
// session, but a page reload re-evaluates this module and clears the cache,
// which is exactly the "preserve on close+open / clear on reload" semantic
// the cashier flow wants.
const __employeeCache = {};

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const fmtINR = (n) => Number(n || 0).toFixed(2);

// Pulls the discount value out of any verdict shape — different evaluators
// surface it under slightly different keys (discount / amount / total_discount).
const verdictDiscount = (v) => {
  if (!v) return 0;
  return Number(v.discount ?? v.total_discount ?? v.amount ?? 0) || 0;
};

const schemeTypeLabel = (t) =>
  ({
    free_slab: "Free item",
    disc_material: "Material discount",
    disc_bill_pct: "Bill %",
    disc_bill_amt: "Bill flat ₹",
    combo: "Combo",
    employee: "Employee discount",
  }[t] || t);

const SchemePanel = ({
  cmrId,
  phoneE164,
  storeId,
  cashierId,
  billTotal,
  billRef,
  setFieldValue,
  values,
  prefixName = "sot",
}) => {
  // ----- in-memory module cache + per-bill storage --------------------------
  //   1. Per-bill draft state (selection / policy / autoLocked / applyState)
  //      keyed by billRef. localStorage so it survives draft reload.
  //   2. Employee verification — module-level cache keyed by customer
  //      signature. Lives in memory only: survives panel close/open (the
  //      Dialog unmounts the panel) but a page reload re-evaluates the JS
  //      module and resets the cache to {} as required.
  const lsKey = useMemo(() => (billRef ? `zota.scheme.${billRef}` : null), [billRef]);
  const cmrSig = `${cmrId || ""}|${phoneE164 || ""}`;
  const readLs = () => {
    if (!lsKey) return null;
    try { return JSON.parse(localStorage.getItem(lsKey) || "null"); } catch { return null; }
  };
  const readEmpLs = () => __employeeCache[cmrSig] || null;

  const [open, setOpen] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [applicable, setApplicable] = useState([]);
  const [considered, setConsidered] = useState(0);   // total schemes the engine looked at
  const [evalError, setEvalError] = useState(null);  // surface auth / network errors in UI
  const [selectedId, setSelectedId] = useState(() => readLs()?.selectedId || null);
  const [autoLocked, setAutoLocked] = useState(() => !!readLs()?.autoLocked);
  const [policy, setPolicy] = useState(() => readLs()?.policy || "one");
  const [applyState, setApplyState] = useState(() => readLs()?.applyState || null); // {scheme_id, bill_offer_application_id, total_discount}
  const [applyError, setApplyError] = useState(null);

  // Employee verification subpanel state. Initial hydration prefers the
  // bill-scoped slot if present, then falls back to the global slot. The
  // useEffect below also re-hydrates when billRef appears mid-mount.
  const [employee, setEmployee] = useState(
    () => readLs()?.employee || readEmpLs() || null
  ); // {id, code, name, phone}
  const [empOpen, setEmpOpen] = useState(false);
  const [empIdentifier, setEmpIdentifier] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [otpStage, setOtpStage] = useState("idle"); // idle | sending | sent | verifying | verified | error
  const [otpError, setOtpError] = useState(null);
  const [otpEmpPreview, setOtpEmpPreview] = useState(null); // {name, phone} between send and verify

  const lastEvalAt = useRef(0);
  const debounceTimer = useRef(null);
  // Track which scheme_id we last wrote the discount for. Without this, every
  // re-evaluate produces a referentially new `selected` object, fires the
  // write effect, mutates total_amount, and re-triggers evaluate — infinite
  // loop. We only re-write when the actual scheme_id (or null) changes.
  const lastWrittenSchemeId = useRef(null);
  // Track which employee_id we've already auto-jumped to an employee scheme
  // for. Without this, every re-evaluate after verification would re-snap
  // selection back to the employee scheme even if the cashier manually
  // picked a different one.
  const autoSnappedForEmployeeId = useRef(null);

  // The total used for evaluate must be the PRE-scheme total. We snapshot it
  // into ${prefix}_original_total_amount the first time we write a discount
  // so feedback loops can't drift the math, but that snapshot goes STALE
  // across cart changes — adding/removing lines doesn't go through us. So we
  // reconcile against the live line sum: if the lines don't add up to the
  // snapshot anymore, the cart has fundamentally changed and we discard it.
  // Exclude scheme-added free rows from the sum — otherwise adding a free
  // line trips the "cart changed" reset below and we live-lock.
  const lineSum = useMemo(() => {
    const rows = Array.isArray(values?.data) ? values.data : [];
    return rows
      .filter((r) => !r?.__scheme_added_by_id)
      .reduce(
        (s, r) =>
          s +
          (Number(r?.item_total_amount) ||
            Number(r?.item_unit_price || 0) * Number(r?.quantity || 0)),
        0
      );
  }, [values?.data]);

  useEffect(() => {
    const stored = Number(values?.[`${prefixName}_original_total_amount`] ?? 0);
    if (stored <= 0 || lineSum <= 0) return;
    // Tolerance accounts for rounding; if the cart line sum differs from the
    // stored "original" by more than ₹1, the cart was edited (new line, qty
    // change, line removed) and the stored snapshot is no longer "original".
    if (Math.abs(lineSum - stored) > 1) {
      setFieldValue(`${prefixName}_original_total_amount`, null);
      lastWrittenSchemeId.current = null; // force re-write of the discount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineSum]);

  const effectiveTotal = useMemo(() => {
    const orig = values?.[`${prefixName}_original_total_amount`];
    if (orig != null && Number(orig) > 0) return Number(orig);
    return Number(billTotal || 0);
  }, [values, prefixName, billTotal]);

  // ----- Build the bill snapshot from the Formik state -----------------------
  const buildBillSnapshot = useCallback(() => {
    const rows = Array.isArray(values?.data) ? values.data : [];
    const lines = rows
      .filter((r) => r && (r.item_id || r.product_id))
      .map((r) => {
        // Per-unit net price = line total / qty — MUST match the formula the
        // backend uses when it re-evaluates the scheme at bill commit
        // (orderService billSnap). Previewing with item_unit_price (per-unit
        // MRP) while the backend priced lines differently made percent/price
        // combo + material scheme discounts diverge → BILL_TOTAL_MISMATCH.
        const qty = Number(r.quantity ?? r.qty ?? 0);
        const lineTotal = Number(r.item_total_amount ?? 0);
        const unitNet =
          qty > 0 && lineTotal > 0
            ? Math.round((lineTotal / qty) * 100) / 100
            : Number(r.item_unit_price ?? r.unit_price ?? 0);
        return {
          product_id: String(r.item_id || r.product_id || ""),
          category_id: r.item_category_id || r.category_id || null,
          name: r.item_name || r.name || "",
          qty,
          unit_price: unitNet,
          mrp: Number(r.item_mrp ?? r.mrp ?? r.item_unit_price ?? 0),
          tax_amount: Number(r.item_tax_amount ?? r.tax_amount ?? 0),
          batch_id: r.batch_id || null,
        };
      });
    return {
      bill_id: billRef || values?.[`${prefixName}_id`] || `draft-${Date.now()}`,
      store_id: storeId || "unknown",
      cashier_id: cashierId || null,
      total: effectiveTotal,
      lines,
      employee: employee ? { id: employee.id, code: employee.code, name: employee.name } : null,
    };
  }, [values, billRef, prefixName, storeId, cashierId, effectiveTotal, employee]);

  // ----- Evaluate (debounced) ------------------------------------------------
  const evaluate = useCallback(async () => {
    const bill = buildBillSnapshot();
    // Bill-level schemes (bill_pct, bill_amt, employee) only need a positive
    // total — they don't read lines. Don't gate eval on lines.length or we'll
    // miss those types when the cart shape doesn't expose values.data.
    if (bill.total <= 0) {
      setApplicable([]);
      setSelectedId(null);
      setConsidered(0);
      setEvalError(null);
      return;
    }
    const callId = ++lastEvalAt.current;
    setEvaluating(true);
    setEvalError(null);
    try {
      const res = await api.post(
        `/schemes/evaluate`,
        JSON.stringify({
          bill,
          cmr: {
            customer_id: cmrId || null,
            cmr_phone: phoneE164 || null,
            tier_code: values?.cmr_tier_code || null,
          },
          policy,
        })
      );
      if (callId !== lastEvalAt.current) return;
      const data = res?.data?.data || {};
      const rows = Array.isArray(data.applicable) ? data.applicable : [];
      setApplicable(rows);
      setConsidered(Number(data.considered ?? 0));
      // Skip auto-select when the benefit-policy gate would reject — this
      // can happen if a coupon or loyalty redemption is already on the bill
      // and the policy is 'one'. The cashier still sees the applicable list
      // and can swap manually (the row's onClick re-checks the gate).
      const policyName = resolvePolicy(window.__loyaltyMember);
      const gateOk = canAdd("scheme", values, policyName).ok;
      if (!autoLocked) {
        setSelectedId(gateOk ? rows[0]?.scheme_id || null : null);
      } else if (selectedId && !rows.some((r) => r.scheme_id === selectedId)) {
        setSelectedId(gateOk ? rows[0]?.scheme_id || null : null);
        setAutoLocked(false);
      }
    } catch (err) {
      if (callId !== lastEvalAt.current) return;
      const body = err?.response?.data || {};
      setEvalError(body.message || err?.message || "Evaluate failed");
      setApplicable([]);
      setConsidered(0);
      console.warn("[SchemePanel] evaluate failed:", body, "request body:",
        { bill: bill.bill_id, total: bill.total, lines: bill.lines.length });
    } finally {
      if (callId === lastEvalAt.current) setEvaluating(false);
    }
  }, [buildBillSnapshot, cmrId, phoneE164, values?.cmr_tier_code, policy, autoLocked, selectedId]);

  // Debounce evaluate on cart / total / employee changes. Use effectiveTotal
  // (pre-scheme) so writing the discount doesn't feed back into the dep.
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => evaluate(), 350);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTotal, values?.data, employee, policy]);

  // Persist per-bill UI state so refresh restores the cashier's choice.
  useEffect(() => {
    if (!lsKey) return;
    const snap = { selectedId, autoLocked, policy, employee, applyState };
    try { localStorage.setItem(lsKey, JSON.stringify(snap)); } catch {}
  }, [lsKey, selectedId, autoLocked, policy, employee, applyState]);

  // Mirror the verified employee into the module cache so close+reopen of
  // the side panel restores it. The cache is wiped automatically on page
  // reload (module re-evaluates) and on customer switch (the cmrSig key
  // changes; old keys are deleted below).
  useEffect(() => {
    if (employee) __employeeCache[cmrSig] = employee;
    else delete __employeeCache[cmrSig];
  }, [employee, cmrSig]);

  // Customer-change reset: when (cmrId, phoneE164) changes mid-session,
  // wipe the local chip AND purge every other customer's cached entry. The
  // mount-time hydration uses the new key so a legitimate re-mount on the
  // same customer still restores from the cache.
  const lastCustomerSigRef = useRef(cmrSig);
  useEffect(() => {
    if (lastCustomerSigRef.current === cmrSig) return;
    lastCustomerSigRef.current = cmrSig;
    setEmployee(null);
    Object.keys(__employeeCache).forEach((k) => {
      if (k !== cmrSig) delete __employeeCache[k];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmrSig]);

  // Re-hydrate when billRef arrives AFTER mount. The useState initializer
  // saw a null lsKey and skipped per-bill restore; now that we have one,
  // pull in selectedId / policy / autoLocked / employee / applyState if the
  // localStorage row is richer than what we currently hold.
  useEffect(() => {
    if (!lsKey) return;
    const snap = readLs();
    if (!snap) return;
    if (snap.selectedId != null && selectedId == null) setSelectedId(snap.selectedId);
    if (snap.autoLocked != null && !autoLocked) setAutoLocked(!!snap.autoLocked);
    if (snap.policy && snap.policy !== policy) setPolicy(snap.policy);
    if (snap.employee && !employee) setEmployee(snap.employee);
    if (snap.applyState && !applyState) setApplyState(snap.applyState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lsKey]);

  // ----- Write the chosen scheme's discount to Formik ------------------------
  const selected = useMemo(
    () => applicable.find((r) => r.scheme_id === selectedId) || null,
    [applicable, selectedId]
  );

  const writeDiscountLine = useCallback(
    (row) => {
      if (typeof setFieldValue !== "function") return;
      if (!row) {
        setFieldValue("schemeDiscount", null);
        setFieldValue(`${prefixName}_scheme_discount`, 0);
        // Recompute with scheme zeroed out — keeps any active coupon / points
        // discount in the total. Restoring to original would wrongly drop them.
        recomputeBillTotal({
          values,
          setFieldValue,
          prefixName,
          override: { scheme: 0 },
        });
        return;
      }
      const discount = round2(verdictDiscount(row.verdict));
      const line = {
        scheme_id: row.scheme_id,
        scheme_name: row.name,
        scheme_type: row.type,
        discount_amount: discount,
        verdict: row.verdict,
        bill_ref: billRef || null,
        // Stacking policy the cashier picked from the panel toggle (one /
        // two / all). Bill-save dispatch reads this into benefits.scheme.policy
        // — without it, the backend service defaults to 'one' and silently
        // refuses to stack even when the cashier explicitly chose 'two'/'all'.
        policy,
        // Employee context, if any — required for the 'employee' scheme type
        // evaluator. Stays null on walk-in / non-employee bills.
        employee: employee || null,
      };
      setFieldValue("schemeDiscount", line);
      setFieldValue(`${prefixName}_scheme_discount`, discount);
      // Shared recompute keeps coupon + scheme + points consistent. Passing
      // the just-applied discount via override.scheme because setFieldValue
      // above is async-batched and `values.schemeDiscount` still holds the
      // old value during this same tick.
      recomputeBillTotal({
        values,
        setFieldValue,
        prefixName,
        override: { scheme: discount },
      });
    },
    [setFieldValue, prefixName, billRef, values, policy, employee]
  );

  // Sync the free-product cart line for schemes that grant one (free_slab,
  // combo with a 'free' benefit). Unlike the first version — which pushed a
  // skeletal row that serialised as `Invalid date`/`NaN` in the bill payload
  // — this HYDRATES a real product + batch through the same APIs the
  // promotional-offer free-item flow uses, so the row passes the submit
  // mapper and backend Joi, and inventory correctly deducts the freebie.
  //
  // Batch pick honours scheme config.eligible_batches when set; otherwise
  // the first in-stock batch. Row is tagged __scheme_added_by_id so
  // switching/clearing the scheme strips it. quantity=1 with all price
  // fields 0 → gross bill total unchanged; the reward is ALSO recorded
  // server-side in bill_offer_application at commit.
  const injectSeqRef = useRef(0);
  const syncSchemeLines = useCallback(
    async (schemeRow) => {
      if (typeof setFieldValue !== "function") return;
      const seq = ++injectSeqRef.current;
      const rows = Array.isArray(values?.data) ? values.data : [];
      const stripped = rows.filter((r) => !r?.__scheme_added_by_id);

      // Which free product (if any) does the selected scheme grant?
      let freeProductId = null;
      let freeProductName = null;
      if (schemeRow?.type === "free_slab") {
        const pool = schemeRow?.verdict?.free_product_choice_pool || [];
        freeProductId = pool[0]?.product_id || null;
        freeProductName = pool[0]?.name || null;
      } else if (
        schemeRow?.type === "combo" &&
        schemeRow?.verdict?.benefit?.benefit_type === "free"
      ) {
        freeProductId = schemeRow.verdict.benefit.free_product_id || null;
      }

      if (!freeProductId) {
        if (stripped.length !== rows.length) setFieldValue("data", stripped);
        return;
      }
      // Already injected for this scheme? Leave it alone.
      const existing = rows.find(
        (r) =>
          r?.__scheme_added_by_id === schemeRow.scheme_id &&
          String(r?.item_id) === String(freeProductId)
      );
      if (existing) return;

      try {
        const [stockRes, productRes] = await Promise.all([
          api.get(`/product/itemBatch/getBatchNumberItemId?item_id=${freeProductId}`),
          api.get(`/product/get?field=item_id&value=${freeProductId}`),
        ]);
        // A newer selection superseded this fetch — drop the result.
        if (seq !== injectSeqRef.current) return;
        const batches = stockRes?.data?.data || [];
        if (batches.length === 0) {
          errorToast(
            `Free item "${freeProductName || freeProductId}" is out of stock — reward not added`
          );
          if (stripped.length !== rows.length) setFieldValue("data", stripped);
          return;
        }
        // Prefer a batch the scheme explicitly allows (eligible_batches ships
        // on the /evaluate summary; config is the fallback for raw rows).
        const eligible = (
          schemeRow?.eligible_batches ||
          schemeRow?.config?.eligible_batches ||
          []
        ).map(String);
        const batch =
          (eligible.length > 0 &&
            batches.find((b) => eligible.includes(String(b?.item_batch_number)))) ||
          batches[0];
        const productInfo = productRes?.data?.data?.[0] || {};

        const freeRow = {
          item_id: freeProductId,
          item_code: productInfo?.item_code || "",
          item_name: batch?.item_name || productInfo?.item_name || freeProductName || "Free item",
          item_generic_name: productInfo?.item_generic_name || "",
          item_manufacturer_name: productInfo?.item_manufacturer_name || "",
          item_manufacturer_id: productInfo?.item_manufacturer_id || "",
          item_batch_number: batch?.item_batch_number || "",
          item_exp_date: batch?.item_exp_date
            ? String(batch.item_exp_date).slice(0, 10)
            : "",
          item_rack_location: batch?.item_rack_location || "",
          item_uom: batch?.item_uom || "PCS",
          item_uom_id: batch?.item_uom_id || "",
          item_unit_price: 0,
          item_price_wiithout_tax: 0,
          item_selling_price: 0,
          quantity: 1,
          item_pack_qty: 0,
          item_loose_qty: 1,
          item_free_quantity: 0,
          item_discount_amount: 0,
          item_discount_percentage: 100,
          item_tax_amount: 0,
          item_gst: 0,
          item_sgst: 0,
          item_cgst: 0,
          item_igst: 0,
          item_total_amount: 0,
          item_final_price: 0,
          item_margin_percentage: 0,
          item_total_tax_percentage: parseFloat(batch?.tax_rate) || 0,
          item_schedule: batch?.item_schedule || productInfo?.item_schedule || "",
          item_hsn: productInfo?.item_hsn || "",
          item_batch_final_purchase_rate: batch?.item_batch_final_purchase_rate || 0,
          to_bin_id: batch?.to_bin_id || "",
          instock: true,
          // NOT is_promotional — the promotional-offer machinery splices out
          // rows flagged is_promotional when its own offers don't match;
          // scheme rewards are tracked by __scheme_added_by_id instead.
          is_promotional: false,
          po_id: null,
          is_predefined_dis: true,
          __scheme_added_by_id: schemeRow.scheme_id,
          __scheme_kind: schemeRow.type === "combo" ? "combo_free_reward" : "free_slab_reward",
        };
        setFieldValue("data", [...stripped, freeRow]);
        successToast(`Free item "${freeRow.item_name}" added (scheme reward)`);
      } catch (err) {
        console.warn("[SchemePanel] free-item hydration failed:", err);
        errorToast("Couldn't add the scheme's free item — add it manually if owed");
        if (stripped.length !== rows.length) setFieldValue("data", stripped);
      }
    },
    [setFieldValue, values?.data]
  );

  // Apply the selected scheme to the form ONLY when the chosen scheme_id
  // actually changes — every re-evaluate produces a referentially new
  // `selected` object but identity of the choice hasn't changed; rewriting
  // the discount line on every tick would re-trigger the parent's total
  // recalc and live-lock the panel after "Proceed to payments".
  useEffect(() => {
    const nextId = selected?.scheme_id ?? null;
    if (lastWrittenSchemeId.current === nextId) return;
    lastWrittenSchemeId.current = nextId;
    writeDiscountLine(selected);
    syncSchemeLines(selected);
  }, [selected, writeDiscountLine, syncSchemeLines]);

  // Atomic-apply: scheme audit row (bill_offer_application) is written by
  // the backend INSIDE the bill-INSERT transaction. The bill-save dispatch
  // ships benefits.scheme = { scheme_id, policy } and the controller calls
  // applyAtBill(client, …) on the same client. No more post-save POST to
  // /schemes/apply — it would race the parent's resetForm() on unmount AND
  // leave audit / total inconsistent if either side fails.
  //
  // Setting `selected` (above) still writes the discount line to Formik so
  // the cashier sees the reduced total live. Backend re-evaluates the
  // scheme against the trusted bill snapshot at commit time and uses ITS
  // canonical discount amount in the bill row — frontend-side number is
  // for display only.

  // When the selection is cleared, drop the stale apply marker (window
  // breadcrumb the bill-save flow used to read).
  useEffect(() => {
    if (!selected && applyState) {
      setApplyState(null);
      window.__lastSchemeApplied = null;
    }
  }, [selected, applyState]);

  // Auto-snap to the employee scheme the FIRST time an employee gets verified
  // (i.e. employee.id changes). Without this, the cashier verifies and then
  // wonders why nothing applied — the employee scheme is in the dropdown but
  // sits below higher-priority schemes that auto-locked first. We only snap
  // once per employee_id so manual overrides afterwards stick.
  useEffect(() => {
    if (!employee?.id) {
      autoSnappedForEmployeeId.current = null;
      return;
    }
    if (autoSnappedForEmployeeId.current === employee.id) return;
    const empScheme = applicable.find((r) => r.type === "employee");
    if (!empScheme) return;
    autoSnappedForEmployeeId.current = employee.id;
    setSelectedId(empScheme.scheme_id);
    setAutoLocked(true);
  }, [employee?.id, applicable]);

  // ----- Employee OTP subflow ------------------------------------------------
  const sendEmployeeOtp = async () => {
    const identifier = empIdentifier.trim();
    if (!identifier) return;
    setOtpStage("sending");
    setOtpError(null);
    try {
      const r = await api.post(
        `/employees/otp/send`,
        JSON.stringify({ identifier, store_id: storeId || null })
      );
      const d = r?.data?.data || {};
      setOtpEmpPreview({ id: d.employee_id, code: d.employee_code, name: d.name, phone: d.phone });
      setOtpStage("sent");
      successToast(`OTP sent to ${d.phone}`);
    } catch (err) {
      const body = err?.response?.data || {};
      setOtpError(body.message || "Send OTP failed");
      setOtpStage("error");
    }
  };

  const verifyEmployeeOtp = async () => {
    const identifier = empIdentifier.trim();
    const otp = otpInput.trim();
    if (!identifier || !otp) return;
    setOtpStage("verifying");
    setOtpError(null);
    try {
      const r = await api.post(
        `/employees/otp/verify`,
        JSON.stringify({ identifier, otp })
      );
      const d = r?.data?.data || {};
      setEmployee({ id: d.employee_id, code: d.employee_code, name: d.name, phone: d.phone });
      setOtpStage("verified");
      successToast(`Employee ${d.employee_code} verified`);
      // Close the subpanel — evaluate will re-run from the employee dep.
      setTimeout(() => setEmpOpen(false), 300);
    } catch (err) {
      const body = err?.response?.data || {};
      setOtpError(body.message || "OTP verification failed");
      setOtpStage("error");
    }
  };

  const clearEmployee = () => {
    setEmployee(null);
    setEmpIdentifier("");
    setOtpInput("");
    setOtpStage("idle");
    setOtpError(null);
    setOtpEmpPreview(null);
  };

  // ----- Render --------------------------------------------------------------
  const hasApplicable = applicable.length > 0;
  const selectedDiscount = selected ? verdictDiscount(selected.verdict) : 0;

  return (
    <div className="mb-2.5 bg-white border border-[#E6E9EF] rounded-md">
      {/* Header row — always visible */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
      >
        <div className="w-7 h-7 rounded-md bg-[#F0E9FF] text-[#7C3AED] flex items-center justify-center">
          <Layers size={14} />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-bold text-[#0F172A]">
            Schemes
            {selected && (
              <span className="ml-2 inline-flex items-center gap-1 text-[#15803C]">
                <CheckCircle2 size={12} /> {selected.name}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#64748B]">
            {evalError ? (
              <span className="text-[#B91C1C]">Eval error: {evalError}</span>
            ) : evaluating ? (
              "Checking eligibility…"
            ) : hasApplicable ? (
              <>
                {applicable.length} applicable — ₹{fmtINR(selectedDiscount)} off
                {applyState && applyState.scheme_id === selected?.scheme_id ? " · audited" : ""}
              </>
            ) : considered > 0 ? (
              `Evaluated ${considered} active — none match this cart`
            ) : (
              "No active schemes"
            )}
            {applyError && (
              <span className="ml-2 text-[#B91C1C]">· audit failed</span>
            )}
          </div>
        </div>
        <ChevronDown
          size={14}
          className={`text-[#94A3B8] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-[#EEF1F5] p-3 space-y-3">
          {/* Stacking policy */}
          <div className="flex items-center gap-2 text-[11.5px]">
            <span className="text-[#64748B]">Stacking:</span>
            {["one", "two", "all"].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPolicy(p)}
                className={`px-2 py-0.5 rounded font-semibold ${
                  policy === p
                    ? "bg-[#E0E7FF] text-primary"
                    : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E6E9EF]"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Dropdown of applicable schemes */}
          <div className="space-y-1.5">
            {!hasApplicable && (
              <div className="text-[12px] text-[#64748B] px-2 py-3 bg-[#F8FAFC] rounded">
                {evalError ? (
                  <>
                    <div className="text-[#B91C1C] font-semibold mb-1">Could not reach scheme engine</div>
                    <div className="text-[11.5px]">{evalError}</div>
                  </>
                ) : considered > 0 ? (
                  <>
                    <div className="font-semibold mb-1">{considered} active scheme{considered === 1 ? "" : "s"} considered — none match</div>
                    <div className="text-[11.5px]">
                      Likely reasons: bill total below the lowest slab, scope filter (category / SKU) doesn't match the cart, or this scheme type needs an employee verification.
                    </div>
                  </>
                ) : (
                  <>No active schemes right now. Activate one in Settings → Promotions → Schemes.</>
                )}
              </div>
            )}
            {applicable.map((row, idx) => {
              const isSel = row.scheme_id === selectedId;
              const isEmployee = row.type === "employee";
              const d = round2(verdictDiscount(row.verdict));
              return (
                <button
                  key={row.scheme_id}
                  type="button"
                  onClick={() => {
                    // Benefit-policy gate before adding a scheme — same
                    // limit as coupons/points. Allowed if a scheme is
                    // already active (replacement) or count < limit.
                    const policyName = resolvePolicy(window.__loyaltyMember);
                    const ok = canAdd("scheme", values, policyName);
                    if (!ok.ok) {
                      errorToast(ok.reason);
                      return;
                    }
                    setSelectedId(row.scheme_id);
                    setAutoLocked(true);
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded border text-left ${
                    isSel
                      ? "border-primary bg-[#F8FAFF]"
                      : isEmployee
                      ? "border-[#FBC78D] bg-[#FFF8EE] hover:bg-[#FFF3E0]"
                      : "border-[#E6E9EF] hover:bg-[#FAFBFC]"
                  }`}
                >
                  <div className="w-6 h-6 rounded-full bg-white border border-[#E6E9EF] flex items-center justify-center text-[10px] font-bold text-[#64748B]">
                    {idx === 0 && !autoLocked ? "★" : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#0F172A] truncate flex items-center gap-1.5">
                      {row.name}
                      {isEmployee && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider rounded bg-[#FBC78D] text-[#7C2D12] shrink-0">
                          Employee
                        </span>
                      )}
                    </div>
                    <div className="text-[10.5px] text-[#64748B]">
                      {schemeTypeLabel(row.type)} · priority {row.priority}
                    </div>
                  </div>
                  <div className="text-[12.5px] font-bold text-[#15803C] whitespace-nowrap">
                    − ₹{fmtINR(d)}
                  </div>
                </button>
              );
            })}
            {selected && (
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null);
                  setAutoLocked(true);
                }}
                className="w-full text-[11.5px] text-[#64748B] hover:text-[#0F172A] px-2 py-1.5"
              >
                Clear scheme
              </button>
            )}
          </div>

          {/* Employee verification block — visible when no employee linked yet */}
          {!employee ? (
            <div className="border-t border-[#EEF1F5] pt-2.5">
              <button
                type="button"
                onClick={() => setEmpOpen((o) => !o)}
                className="w-full flex items-center gap-2 text-[12px] font-semibold text-[#475569] hover:text-[#0F172A]"
              >
                <Sparkles size={12} className="text-[#B7791F]" />
                Verify employee (for employee-only schemes)
                <ChevronDown
                  size={12}
                  className={`ml-auto transition-transform ${empOpen ? "rotate-180" : ""}`}
                />
              </button>

              {empOpen && (
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    placeholder="Employee code or phone"
                    value={empIdentifier}
                    onChange={(e) => {
                      setEmpIdentifier(e.target.value);
                      if (otpStage !== "idle") {
                        setOtpStage("idle");
                        setOtpInput("");
                      }
                    }}
                    className="w-full px-2.5 py-1.5 text-[12.5px] border border-[#E6E9EF] rounded"
                  />
                  {otpStage === "idle" || otpStage === "sending" || otpStage === "error" ? (
                    <button
                      type="button"
                      onClick={sendEmployeeOtp}
                      disabled={!empIdentifier.trim() || otpStage === "sending"}
                      className="w-full px-2.5 py-1.5 text-[12px] font-semibold text-white bg-primary rounded disabled:opacity-50"
                    >
                      {otpStage === "sending" ? "Sending…" : "Send OTP"}
                    </button>
                  ) : (
                    <>
                      {otpEmpPreview && (
                        <div className="text-[11.5px] text-[#475569]">
                          OTP sent to <b>{otpEmpPreview.name}</b> ({otpEmpPreview.phone})
                        </div>
                      )}
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter 6-digit OTP"
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="w-full px-2.5 py-1.5 text-[12.5px] border border-[#E6E9EF] rounded tracking-widest"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={verifyEmployeeOtp}
                          disabled={!otpInput.trim() || otpStage === "verifying"}
                          className="flex-1 px-2.5 py-1.5 text-[12px] font-semibold text-white bg-[#15803C] rounded disabled:opacity-50"
                        >
                          {otpStage === "verifying" ? "Verifying…" : "Verify"}
                        </button>
                        <button
                          type="button"
                          onClick={sendEmployeeOtp}
                          className="px-2.5 py-1.5 text-[12px] font-semibold text-[#475569] bg-[#F1F5F9] rounded hover:bg-[#E6E9EF]"
                        >
                          Resend
                        </button>
                      </div>
                    </>
                  )}
                  {otpError && (
                    <div className="text-[11.5px] text-[#B91C1C]">{otpError}</div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="border-t border-[#EEF1F5] pt-2.5 flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#15803C]" />
              <div className="flex-1 text-[12px]">
                <span className="font-semibold text-[#0F172A]">{employee.name}</span>
                <span className="text-[#64748B]"> · {employee.code}</span>
              </div>
              <button
                type="button"
                onClick={clearEmployee}
                className="text-[#64748B] hover:text-[#B91C1C]"
                title="Remove employee"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SchemePanel;
